import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import { Icon } from '../../components/Icon.tsx';
import { maskedCode, type GiftCardView } from '../../components/Loyalty.tsx';
import { Button, Dialog, EmptyState, Field, Skeleton, StatusBadge, Switch } from '../../components/ui.tsx';
import { t, tn } from '../../i18n/index.ts';
import { action } from '../../lib/api.ts';
import { dateTime, day, localDay, money, todayIso } from '../../lib/format.ts';
import { useSession } from '../../lib/session.tsx';
import { useUi } from '../../lib/ui-state.tsx';
import { useRegister } from '../Pay.tsx';
import { ManageLayout, useAction, useSave } from './common.tsx';
import { Promotions } from './Promotions.tsx';

interface Settings {
  points_enabled: boolean;
  earn_per: number;
  point_value: number;
  min_redeem: number;
  gift_cards_enabled: boolean;
  gift_months: number;
}
interface Overview {
  settings: Settings & { id: string | null };
  points: { members: number; outstanding: number; value: number; earned_30d: number; used_30d: number };
  gift_cards: { live: number; owed: number; expired: number };
}
interface CardRow {
  id: string;
  code: string | null;
  tail: string;
  initial: number;
  balance: number;
  state: 'active' | 'void';
  expires_on: string;
  expired: boolean;
  customer: string | null;
  created_at: string;
}
interface CardDetail {
  card: GiftCardView;
  history: { id: string; kind: 'sale' | 'redeem' | 'refund' | 'void' | 'adjust'; amount: number; method: 'cash' | 'card' | null; note: string | null; at: string; receipt_no: number | null; order_name: string | null; by_name: string | null }[];
}

const FILTERS = ['live', 'used', 'expired', 'void', 'all'] as const;
type Filter = (typeof FILTERS)[number];

/** Points for regular guests and gift cards: what is owed, the cards sold, and the programme's rules. */
export function Loyalty() {
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') === 'points' ? 'points' : params.get('tab') === 'promos' ? 'promos' : 'cards';
  const { data, reload } = useAction<Overview>('loyalty.overview');
  return (
    <ManageLayout title={t('manage.loyalty')} subtitle={t('loyalty.subtitle')}>
      {!data ? (
        <Skeleton h={120} r="var(--radius-lg)" />
      ) : (
        <section className="rep-kpis loyalty-kpis" aria-label={t('loyalty.summary')}>
          <Kpi label={t('loyalty.k_owed')} value={money(data.gift_cards.owed)} foot={tn('loyalty.k_live', data.gift_cards.live)} />
          <Kpi
            label={t('loyalty.k_expired')}
            value={money(data.gift_cards.expired)}
            foot={data.gift_cards.expired ? t('loyalty.k_expired_hint') : t('loyalty.k_none_expired')}
          />
          <Kpi label={t('loyalty.k_points')} value={String(data.points.outstanding)} foot={t('loyalty.k_points_value', { amount: money(data.points.value), n: data.points.members })} />
          <Kpi label={t('loyalty.k_month')} value={`+${data.points.earned_30d}`} foot={t('loyalty.k_month_used', { n: data.points.used_30d })} />
        </section>
      )}
      <div className="segmented tabs" role="tablist" aria-label={t('manage.loyalty')}>
        {(['cards', 'promos', 'points'] as const).map((k) => (
          <button key={k} role="tab" aria-selected={tab === k} className={tab === k ? 'active' : ''} onClick={() => setParams(k === 'cards' ? {} : { tab: k }, { replace: true })}>
            <Icon name={k === 'cards' ? 'gift' : k === 'promos' ? 'percent' : 'star'} size={16} />
            {t(`loyalty.tab_${k}`)}
          </button>
        ))}
      </div>
      {tab === 'cards' ? <GiftCards onChanged={reload} /> : tab === 'promos' ? <Promotions /> : data ? <Programme settings={data.settings} onSaved={reload} /> : <Skeleton h={300} />}
    </ManageLayout>
  );
}

function Kpi({ label, value, foot }: { label: string; value: string; foot: string }) {
  return (
    <div className="rep-kpi">
      <span className="rep-kpi-label">{label}</span>
      <span className="rep-kpi-value">{value}</span>
      <span className="rep-kpi-foot muted">{foot}</span>
    </div>
  );
}

function GiftCards({ onChanged }: { onChanged: () => void }) {
  const { fail } = useUi();
  const [filter, setFilter] = useState<Filter>('live');
  const [search, setSearch] = useState('');
  const [rows, setRows] = useState<CardRow[] | null>(null);
  const [version, setVersion] = useState(0);
  const [open, setOpen] = useState<string | null>(null);
  useEffect(() => {
    const timer = setTimeout(() => void action<CardRow[]>('loyalty.cards', { filter, search: search.trim() || undefined }).then(setRows, fail), search ? 200 : 0);
    return () => clearTimeout(timer);
  }, [filter, search, version, fail]);

  return (
    <>
      <div className="toolbar">
        <div className="toolbar-group">
          <div className="search">
            <Icon name="search" size={18} />
            <input
              className="input"
              type="search"
              value={search}
              placeholder={t('gift.search')}
              aria-label={t('gift.search')}
              onChange={(e) => setSearch(e.target.value)}
              autoComplete="off"
            />
          </div>
        </div>
        <div className="chips" role="radiogroup" aria-label={t('gift.filter')}>
          {FILTERS.map((f) => (
            <button key={f} role="radio" aria-checked={filter === f} className={`chip${filter === f ? ' active' : ''}`} onClick={() => setFilter(f)}>
              {t(`gift.f_${f}`)}
            </button>
          ))}
        </div>
      </div>
      {!rows ? (
        <Skeleton h={240} r="var(--radius-lg)" />
      ) : rows.length === 0 ? (
        <EmptyState icon="gift" title={search ? t('gift.none_found', { q: search }) : t(`gift.none_${filter}`)} body={filter === 'live' && !search ? t('gift.none_body') : undefined} />
      ) : (
        <div className="list-card" role="list">
          {rows.map((c) => (
            <div key={c.id} role="listitem" className="list-row gift-row">
              <span className={`gift-badge${c.state === 'void' || c.expired || !c.balance ? ' spent' : ''}`} aria-hidden="true">
                <Icon name="gift" size={20} />
              </span>
              <button className="list-main" onClick={() => setOpen(c.id)}>
                <span className="list-title mono">{maskedCode(c)}</span>
                <span className="list-sub">
                  <span>{t('gift.sold_on', { date: localDay(c.created_at) })}</span>
                  {c.customer ? <span>· {c.customer}</span> : null}
                  <span className="gift-sub-state">
                    <CardState card={c} />
                  </span>
                </span>
              </button>
              <span className="row-end">
                <span className="strong">{money(c.balance)}</span>
                <span className="muted">{c.balance !== c.initial ? t('gift.of_initial', { amount: money(c.initial) }) : t('gift.unused')}</span>
              </span>
              <span className="gift-row-state">
                <CardState card={c} />
              </span>
            </div>
          ))}
        </div>
      )}
      {open ? (
        <CardDialog
          id={open}
          onClose={() => setOpen(null)}
          onChanged={() => {
            setVersion((v) => v + 1);
            onChanged();
          }}
        />
      ) : null}
    </>
  );
}

function CardState({ card }: { card: Pick<CardRow, 'state' | 'expired' | 'balance' | 'expires_on'> }) {
  if (card.state === 'void') return <StatusBadge tone="neutral" icon="x" label={t('gift.state_void')} />;
  if (card.expired) return <StatusBadge tone="warn" icon="clock" label={t('gift.state_expired')} />;
  if (!card.balance) return <StatusBadge tone="neutral" icon="check" label={t('gift.state_used')} />;
  return <StatusBadge tone="ok" icon="checkCircle" label={t('gift.until_short', { date: day(card.expires_on) })} />;
}

const ENTRY_LABEL = {
  sale: 'gift.h_sale',
  redeem: 'gift.h_redeem',
  refund: 'gift.h_refund',
  void: 'gift.h_void',
  adjust: 'gift.h_adjust',
} as const;

function CardDialog({ id, onClose, onChanged }: { id: string; onClose: () => void; onChanged: () => void }) {
  const { can } = useSession();
  const { fail } = useUi();
  const [d, setD] = useState<CardDetail | null>(null);
  const [voiding, setVoiding] = useState(false);
  const [extending, setExtending] = useState(false);
  const load = () => void action<CardDetail>('loyalty.card', { card: id }).then(setD, fail);
  useEffect(load, [id]);
  const c = d?.card;
  const unused = !!d && d.history.every((h) => h.kind === 'sale') && c?.state === 'active';

  return (
    <Dialog
      title={c ? t('gift.card_tail', { tail: c.tail }) : t('common.loading')}
      onClose={onClose}
      footer={
        c && can('loyalty.manage') && c.state === 'active' ? (
          <>
            {unused ? (
              <Button variant="ghost" icon="x" onClick={() => setVoiding(true)}>
                {t('gift.void')}
              </Button>
            ) : null}
            <Button variant="secondary" icon="clock" onClick={() => setExtending(true)}>
              {t('gift.extend')}
            </Button>
          </>
        ) : undefined
      }
    >
      {!d || !c ? (
        <Skeleton h={260} />
      ) : (
        <div className="gift-detail">
          <div className={`gift-slip compact${c.usable ? '' : ' spent'}`}>
            <span className="gift-slip-title">
              <Icon name="gift" size={18} /> {t('gift.slip_title')}
            </span>
            <strong className="gift-slip-amount">{money(c.balance)}</strong>
            <span className="gift-slip-meta">{c.balance !== c.initial ? t('gift.of_initial', { amount: money(c.initial) }) : t('gift.unused')}</span>
            <span className="gift-slip-code mono">{maskedCode(c)}</span>
          </div>
          <dl className="report kv">
            <dt>{t('gift.state')}</dt>
            <dd>
              <CardState card={c} />
            </dd>
            <dt>{t('gift.valid_until_label')}</dt>
            <dd>{day(c.expires_on)}</dd>
            {c.customer ? (
              <>
                <dt>{t('gift.for_customer')}</dt>
                <dd>{c.customer}</dd>
              </>
            ) : null}
            {c.note ? (
              <>
                <dt>{t('gift.note')}</dt>
                <dd>{c.note}</dd>
              </>
            ) : null}
            {c.void_reason ? (
              <>
                <dt>{t('gift.void_reason')}</dt>
                <dd>{c.void_reason}</dd>
              </>
            ) : null}
          </dl>
          <h3>{t('gift.history')}</h3>
          <ul className="rep-reasons">
            {d.history.map((h) => (
              <li key={h.id}>
                <span>
                  <span className="strong">{t(ENTRY_LABEL[h.kind])}</span>
                  <span className="muted">
                    {' · '}
                    {dateTime(h.at)}
                    {h.receipt_no ? ` · ${t('points.on_receipt', { n: h.receipt_no })}` : ''}
                    {h.method ? ` · ${t(`pay.method_${h.method}`)}` : ''}
                    {h.by_name ? ` · ${h.by_name}` : ''}
                    {h.note ? ` · ${h.note}` : ''}
                  </span>
                </span>
                {h.amount ? <span className={`strong ${h.amount > 0 ? 'text-ok' : ''}`}>{money(h.amount, { sign: true })}</span> : <span />}
              </li>
            ))}
          </ul>
        </div>
      )}
      {voiding && c ? (
        <VoidCard
          card={c}
          method={d?.history.find((h) => h.kind === 'sale')?.method ?? null}
          onClose={() => setVoiding(false)}
          onDone={() => {
            setVoiding(false);
            load();
            onChanged();
          }}
        />
      ) : null}
      {extending && c ? (
        <ExtendCard
          card={c}
          onClose={() => setExtending(false)}
          onDone={() => {
            setExtending(false);
            load();
            onChanged();
          }}
        />
      ) : null}
    </Dialog>
  );
}

function VoidCard({ card, method, onClose, onDone }: { card: GiftCardView; method: 'cash' | 'card' | null; onClose: () => void; onDone: () => void }) {
  const { register } = useRegister();
  const { busy, run } = useSave();
  const [reason, setReason] = useState('');
  const session = register?.session ?? null;
  return (
    <Dialog
      title={t('gift.void_title', { tail: card.tail })}
      onClose={onClose}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="danger"
            icon="x"
            busy={busy}
            disabled={!session || reason.trim().length < 3}
            onClick={() =>
              void run(async () => {
                await action('loyalty.void_card', { card: card.id, session: session!.id, reason: reason.trim() });
                onDone();
              }, t('gift.voided', { tail: card.tail }))
            }
          >
            {t('gift.void')}
          </Button>
        </>
      }
    >
      <div className="gift-sell">
        <p>{method === 'cash' ? t('gift.void_cash', { amount: money(card.initial) }) : t('gift.void_card', { amount: money(card.initial) })}</p>
        {!session ? (
          <p className="callout callout-warn">
            <Icon name="alert" size={16} /> {t('gift.void_no_shift')}
          </p>
        ) : null}
        <Field label={t('gift.void_reason')}>
          <input className="input" value={reason} maxLength={200} onChange={(e) => setReason(e.target.value)} autoComplete="off" data-autofocus />
        </Field>
      </div>
    </Dialog>
  );
}

function ExtendCard({ card, onClose, onDone }: { card: GiftCardView; onClose: () => void; onDone: () => void }) {
  const { busy, run } = useSave();
  const later = (months: number) => {
    const base = card.expires_on > todayIso() ? card.expires_on : todayIso();
    const d = new Date(`${base}T12:00:00Z`);
    d.setUTCMonth(d.getUTCMonth() + months);
    return d.toISOString().slice(0, 10);
  };
  const [date, setDate] = useState(later(3));
  const [reason, setReason] = useState('');
  return (
    <Dialog
      title={t('gift.extend_title', { tail: card.tail })}
      onClose={onClose}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            icon="check"
            busy={busy}
            disabled={date <= card.expires_on || reason.trim().length < 3}
            onClick={() =>
              void run(async () => {
                await action('loyalty.extend_card', { card: card.id, expires_on: date, reason: reason.trim() });
                onDone();
              }, t('gift.extended', { date: day(date) }))
            }
          >
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="gift-sell">
        <p className="muted">{t('gift.extend_now', { date: day(card.expires_on) })}</p>
        <div className="chips">
          {[1, 3, 6, 12].map((m) => (
            <button key={m} className={`chip${date === later(m) ? ' active' : ''}`} aria-pressed={date === later(m)} onClick={() => setDate(later(m))}>
              {tn('gift.plus_months', m)}
            </button>
          ))}
        </div>
        <Field label={t('gift.valid_until_label')}>
          <input className="input date-input" type="date" value={date} min={card.expires_on} onChange={(e) => setDate(e.target.value)} />
        </Field>
        <Field label={t('gift.extend_reason')}>
          <input className="input" value={reason} maxLength={200} onChange={(e) => setReason(e.target.value)} autoComplete="off" />
        </Field>
      </div>
    </Dialog>
  );
}

/** The programme's rules, with a worked example so the numbers make sense. */
function Programme({ settings, onSaved }: { settings: Settings; onSaved: () => void }) {
  const { can } = useSession();
  const { busy, run } = useSave();
  const [s, setS] = useState<Settings>(settings);
  const [text, setText] = useState({ earn_per: String(settings.earn_per / 100), point_value: String(settings.point_value / 100) });
  const editable = can('loyalty.manage');
  const set = <K extends keyof Settings>(k: K, v: Settings[K]) => setS((x) => ({ ...x, [k]: v }));
  const lek = (v: string) => Math.round(Number(v.replace(',', '.')) * 100);
  const earnPer = lek(text.earn_per);
  const pointValue = lek(text.point_value);
  const errors = {
    earn_per: !(earnPer >= 100) ? t('loyalty.e_earn') : null,
    point_value: !(pointValue >= 1) ? t('loyalty.e_value') : null,
    min_redeem: !(s.min_redeem >= 1) ? t('loyalty.e_min') : null,
    gift_months: !(s.gift_months >= 1 && s.gift_months <= 120) ? t('loyalty.e_months') : null,
  };
  const invalid = Object.values(errors).some(Boolean);
  const example = 2_000_00;
  const earned = earnPer > 0 ? Math.floor(example / earnPer) : 0;
  const back = earnPer > 0 ? (pointValue / earnPer) * 100 : 0;

  const save = () =>
    void run(async () => {
      await action('loyalty.save_settings', { ...s, earn_per: earnPer, point_value: pointValue });
      onSaved();
    }, t('loyalty.saved'));

  return (
    <div className="loyalty-settings">
      <section className="card">
        <header className="card-head">
          <div>
            <h2>{t('loyalty.points_title')}</h2>
            <span className="muted">{t('loyalty.points_hint')}</span>
          </div>
          <Switch checked={s.points_enabled} onChange={(v) => set('points_enabled', v)} label={t('loyalty.points_on')} disabled={!editable} />
        </header>
        <div className="form-grid">
          <Field label={t('loyalty.earn_per')} hint={t('loyalty.earn_per_hint')} error={errors.earn_per}>
            <input className="input" inputMode="decimal" value={text.earn_per} disabled={!editable} onChange={(e) => setText((x) => ({ ...x, earn_per: e.target.value }))} />
          </Field>
          <Field label={t('loyalty.point_value')} hint={t('loyalty.point_value_hint')} error={errors.point_value}>
            <input className="input" inputMode="decimal" value={text.point_value} disabled={!editable} onChange={(e) => setText((x) => ({ ...x, point_value: e.target.value }))} />
          </Field>
          <Field label={t('loyalty.min_redeem')} hint={t('loyalty.min_redeem_hint')} error={errors.min_redeem}>
            <input className="input" inputMode="numeric" value={s.min_redeem || ''} disabled={!editable} onChange={(e) => set('min_redeem', Number(e.target.value.replace(/\D/g, '')) || 0)} />
          </Field>
        </div>
        {!invalid ? (
          <p className="callout callout-info">
            <Icon name="star" size={16} />
            <span>{t('loyalty.example', { bill: money(example), n: earned, worth: money(earned * pointValue), pct: back.toFixed(back < 10 ? 1 : 0) })}</span>
          </p>
        ) : null}
      </section>
      <section className="card">
        <header className="card-head">
          <div>
            <h2>{t('loyalty.gift_title')}</h2>
            <span className="muted">{t('loyalty.gift_hint')}</span>
          </div>
          <Switch checked={s.gift_cards_enabled} onChange={(v) => set('gift_cards_enabled', v)} label={t('loyalty.gift_on')} disabled={!editable} />
        </header>
        <div className="form-grid">
          <Field label={t('loyalty.gift_months')} hint={t('loyalty.gift_months_hint')} error={errors.gift_months}>
            <input className="input" inputMode="numeric" value={s.gift_months || ''} disabled={!editable} onChange={(e) => set('gift_months', Number(e.target.value.replace(/\D/g, '')) || 0)} />
          </Field>
        </div>
        <p className="muted">{t('loyalty.gift_books')}</p>
      </section>
      {editable ? (
        <div className="form-actions">
          <Button variant="primary" icon="check" busy={busy} disabled={invalid} onClick={save}>
            {t('common.save')}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
